import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router';
import { Download, Trash2, X } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Label } from '../components/ui/Label';
import { Alert } from '../components/ui/Alert';
import { Card } from '../components/ui/Card';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogTrigger,
} from '../components/ui/Dialog';
import { StatusBadge } from '../components/ui/StatusBadge';
import { MockBadge } from '../components/MockBadge';
import { StepUpField } from '../components/StepUpField';
import { EditContactDialog } from '../components/EditContactDialog';
import { ContactCustomObjects } from '../components/ContactCustomObjects';
import { stepUpBody, stepUpCodeReady, stepUpErrorText } from '../lib/stepUp';
import { apiDownload, apiFetch } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatDateTime, formatDateWithYear, formatMoney } from '../lib/format';
import {
  ATTRIBUTE_SOURCE_LABEL,
  consentLabel,
  countryLabel,
  languageLabel,
} from '../lib/contactAttributes';
import { AUTO_LABEL_NAMES } from '../lib/automationLabels';
import {
  ApiError,
  type AttributeSource,
  type AutoLabel,
  type ContactDetailResponse,
  type ChurnRisk,
  type ContactLabel,
  type ContactPrediction,
  type ContactStatus,
} from '../types/api';

const CHURN_RISK_TEXT: Record<ChurnRisk, string> = {
  low: 'Low',
  medium: 'Medium: later than usual',
  high: 'High: well past the usual time',
};

/**
 * K6: estimates from the contact's own order history (simple rules, see
 * packages/core/src/db/tenant/predictions.ts). Shown only when there is
 * enough history, and always labelled as estimates.
 */
function PredictionCard({ prediction }: { prediction: ContactPrediction }) {
  const p = prediction;
  const money = (n: number) => formatMoney(n, p.currency ?? 'EUR');
  const overdue = p.expectedNextOrderAt < Date.now();
  return (
    <Card className="p-4" data-testid="contact-predictions">
      <h2 className="text-sm font-semibold m-0">Estimates</h2>
      <p className="text-muted text-xs m-0 mt-1 mb-3">
        Simple rules on the order history, not a promise. Use them as segment conditions under
        Segments.
      </p>
      <dl className="m-0 flex flex-col gap-2 text-sm">
        <div className="flex justify-between gap-4">
          <dt className="text-muted">Next order</dt>
          <dd className="m-0 text-right">
            {overdue ? 'Was due ' : 'Around '}
            {formatDateWithYear(p.expectedNextOrderAt)}
          </dd>
        </div>
        <div className="flex justify-between gap-4">
          <dt className="text-muted">Reorders</dt>
          <dd className="m-0 text-right">
            Every {Math.round(p.gapDays)} days
            {p.basis === 'shop' ? (
              <span className="block text-xs text-muted">
                One order so far: the shop's typical rhythm
              </span>
            ) : null}
          </dd>
        </div>
        <div className="flex justify-between gap-4">
          <dt className="text-muted">Churn risk</dt>
          <dd className="m-0 text-right">{CHURN_RISK_TEXT[p.churnRisk]}</dd>
        </div>
        <div className="flex justify-between gap-4">
          <dt className="text-muted">Predicted value</dt>
          <dd className="m-0 text-right">
            {money(p.predictedValue)}
            <span className="block text-xs text-muted">
              {money(p.spent)} spent + about {p.expectedOrders.toFixed(1)} orders in the next year
            </span>
          </dd>
        </div>
      </dl>
    </Card>
  );
}

/** "(from an import)": says where a value came from, so staff know whether the contact chose it. */
function SourceNote({ source }: { source: AttributeSource | null }) {
  if (!source) return null;
  return <span className="block text-xs text-muted">{ATTRIBUTE_SOURCE_LABEL[source]}</span>;
}

const STATUS_CONFIG: Record<
  ContactStatus,
  { icon: string; tone: 'success' | 'warning' | 'danger' | 'neutral'; label: string }
> = {
  active: { icon: '✓', tone: 'success', label: 'Active' },
  suppressed: { icon: '⛔', tone: 'neutral', label: 'Suppressed' },
};

const HISTORY_LABELS: Record<string, string> = {
  sent: 'Sent',
  click: 'Click',
  unsubscribe: 'Unsubscribed',
  bounce: 'Bounce',
  complaint: 'Complaint',
  delivery: 'Delivered',
};

/** Same rule as the server (core contactLabels.repo.ts `validateManualLabel`). */
const MANUAL_LABEL_PATTERN = /^[a-z0-9æøå-]{1,40}$/;

function labelText(l: ContactLabel): string {
  return l.source === 'auto' ? (AUTO_LABEL_NAMES[l.label as AutoLabel] ?? l.label) : l.label;
}

function labelErrorText(error: unknown, fallback: string): string {
  if (error instanceof ApiError && error.status === 400 && error.message) return error.message;
  if (error instanceof ApiError && error.status === 403) {
    return 'Only editors and administrators can change tags.';
  }
  return fallback;
}

/**
 * Customer tags on one contact: automatic tags (from orders, read-only here) and
 * manual tags, which editors add and remove. Campaigns can target or exclude both.
 */
function ContactTags({ contactId, labels }: { contactId: string; labels: ContactLabel[] }) {
  const { isEditor } = useAuth();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState('');
  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['contact', contactId] }),
      queryClient.invalidateQueries({ queryKey: ['labels'] }),
    ]);

  const add = useMutation({
    mutationFn: (label: string) =>
      apiFetch(`/contacts/${contactId}/labels`, { method: 'POST', body: { label } }),
    onSuccess: async () => {
      setDraft('');
      await refresh();
    },
  });
  const remove = useMutation({
    mutationFn: (label: string) =>
      apiFetch(`/contacts/${contactId}/labels/${encodeURIComponent(label)}`, {
        method: 'DELETE',
      }),
    onSuccess: refresh,
  });

  const value = draft.trim().toLowerCase();
  const valid = MANUAL_LABEL_PATTERN.test(value);
  const already = labels.some((l) => l.label === value);
  const sorted = [...labels].sort(
    (a, b) => a.source.localeCompare(b.source) || a.label.localeCompare(b.label),
  );

  return (
    <Card className="p-4 flex flex-col gap-3">
      <h2 className="text-sm font-semibold m-0">Tags</h2>
      {sorted.length === 0 ? (
        <p className="text-muted text-sm m-0">No tags yet.</p>
      ) : (
        <ul className="m-0 p-0 list-none flex flex-wrap gap-2" aria-label="Tags on this contact">
          {sorted.map((l) => (
            <li
              key={`${l.source}-${l.label}`}
              className="flex items-center gap-1 rounded-full border border-border bg-card pl-2.5 pr-1 py-0.5 text-[13px]"
            >
              <span>{labelText(l)}</span>
              <span className="text-xs text-muted">{l.source === 'auto' ? 'auto' : 'manual'}</span>
              {l.source === 'manual' && isEditor ? (
                <button
                  type="button"
                  aria-label={`Remove tag ${l.label}`}
                  disabled={remove.isPending}
                  onClick={() => remove.mutate(l.label)}
                  className="grid h-6 w-6 place-items-center rounded-full text-muted hover:bg-border-soft hover:text-text"
                >
                  <X size={13} aria-hidden="true" />
                </button>
              ) : (
                <span className="w-1.5" />
              )}
            </li>
          ))}
        </ul>
      )}
      {isEditor ? (
        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (valid && !already) add.mutate(value);
          }}
        >
          <div className="flex flex-col gap-1.5 min-w-0 flex-grow max-w-[260px]">
            <Label htmlFor="contact-tag">Add a manual tag</Label>
            <Input
              id="contact-tag"
              value={draft}
              maxLength={40}
              placeholder="e.g. demo"
              autoComplete="off"
              onChange={(e) => setDraft(e.target.value)}
            />
          </div>
          <Button type="submit" variant="secondary" disabled={!valid || already || add.isPending}>
            {add.isPending ? 'Adding…' : 'Add tag'}
          </Button>
        </form>
      ) : null}
      {isEditor ? (
        <p className="text-xs text-muted m-0">
          Lowercase letters, digits, æøå and hyphens. Automatic tags come from orders and cannot be
          changed here.
        </p>
      ) : null}
      {already && value ? (
        <p className="text-xs text-muted m-0">The contact already has this tag.</p>
      ) : null}
      {add.isError ? (
        <Alert>{labelErrorText(add.error, 'The tag could not be added.')}</Alert>
      ) : null}
      {remove.isError ? (
        <Alert>{labelErrorText(remove.error, 'The tag could not be removed.')}</Alert>
      ) : null}
    </Card>
  );
}

/** Review fix 25/9: distinct messages instead of "not found" for every failure. */
export function contactLoadErrorMessage(error: unknown): string {
  if (error instanceof ApiError && error.status === 404) return 'The contact was not found.';
  if (error instanceof ApiError && error.status === 403) {
    return 'You do not have access to this contact.';
  }
  return 'The contact could not be loaded. Please try again shortly.';
}

function DeleteContactDialog({ email, contactId }: { email: string; contactId: string }) {
  const [open, setOpen] = useState(false);
  const [confirmEmail, setConfirmEmail] = useState('');
  const [code, setCode] = useState('');
  const navigate = useNavigate();

  const mutation = useMutation({
    // Deleting needs a fresh 2FA code (step-up, pentest 8/10).
    mutationFn: () =>
      apiFetch(`/contacts/${contactId}`, { method: 'DELETE', body: stepUpBody(code) }),
    onSuccess: () => navigate('/kontakter'),
  });

  const canConfirm =
    confirmEmail.trim().toLowerCase() === email.toLowerCase() && stepUpCodeReady(code);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) {
          setConfirmEmail('');
          setCode('');
          mutation.reset();
        }
      }}
    >
      <DialogTrigger asChild>
        <Button variant="danger">
          <Trash2 size={15} aria-hidden="true" />
          Delete contact
        </Button>
      </DialogTrigger>
      <DialogContent
        title="Delete contact permanently"
        description="This removes the contact and all history. This action cannot be undone."
      >
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (canConfirm) mutation.mutate();
          }}
        >
          {mutation.isError ? (
            <Alert>
              {stepUpErrorText(mutation.error) ??
                'The contact could not be deleted. Please try again.'}
            </Alert>
          ) : null}
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="delete-confirm">
              Type the email address <span className="font-mono">{email}</span> to confirm
            </Label>
            <Input
              id="delete-confirm"
              value={confirmEmail}
              onChange={(e) => setConfirmEmail(e.target.value)}
              autoComplete="off"
            />
          </div>
          <StepUpField id="delete-code" value={code} onChange={setCode} />
          <DialogFooter>
            <DialogClose>
              <Button type="button" variant="secondary">
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" variant="danger" disabled={!canConfirm || mutation.isPending}>
              {mutation.isPending ? 'Deleting…' : 'Delete permanently'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function KontaktPage() {
  const { id } = useParams<{ id: string }>();
  const { isAdmin, isEditor } = useAuth();
  const [exportError, setExportError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['contact', id],
    queryFn: ({ signal }) => apiFetch<ContactDetailResponse>(`/contacts/${id}`, { signal }),
    enabled: Boolean(id),
  });

  async function handleExport() {
    if (!id) return;
    setExportError(null);
    setExporting(true);
    try {
      await apiDownload(`/contacts/${id}/export`, `contact-${id}.json`);
    } catch {
      setExportError('The data could not be exported. Please try again.');
    } finally {
      setExporting(false);
    }
  }

  if (isLoading) return <div className="text-muted">Loading contact…</div>;
  if (isError) return <Alert>{contactLoadErrorMessage(error)}</Alert>;
  if (!data) return <Alert>The contact was not found.</Alert>;

  const { contact, consent, history } = data;
  const cfg = STATUS_CONFIG[contact.status];

  return (
    <>
      <header className="flex items-center gap-3">
        <Link to="/kontakter" className="no-underline">
          ← Contacts
        </Link>
        <MockBadge />
      </header>

      <div className="flex flex-wrap items-center gap-3">
        <h1 className="m-0 text-2xl font-semibold tracking-tight break-all">{contact.email}</h1>
        <StatusBadge tone={cfg.tone} icon={cfg.icon}>
          {cfg.label}
        </StatusBadge>
        <div className="ml-auto flex flex-wrap justify-end gap-2">
          <Button variant="secondary" disabled={exporting} onClick={() => void handleExport()}>
            <Download size={15} aria-hidden="true" />
            {exporting ? 'Preparing…' : 'Export data'}
          </Button>
          {isEditor ? <EditContactDialog contact={contact} fields={data.fields ?? {}} /> : null}
          {isAdmin ? <DeleteContactDialog email={contact.email} contactId={contact.id} /> : null}
        </div>
      </div>
      {exportError ? <Alert>{exportError}</Alert> : null}

      <div className="grid grid-cols-2 max-[860px]:grid-cols-1 gap-3">
        <Card className="p-4">
          <h2 className="text-sm font-semibold m-0 mb-3">Details</h2>
          <dl className="m-0 flex flex-col gap-2 text-sm">
            <div className="flex justify-between gap-4">
              <dt className="text-muted">First name</dt>
              <dd className="m-0 text-right">
                {contact.firstName ?? '—'}
                <SourceNote source={contact.nameSource} />
              </dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-muted">Last name</dt>
              <dd className="m-0 text-right">{contact.lastName ?? '—'}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-muted">Language</dt>
              <dd className="m-0 text-right" data-testid="contact-language">
                {languageLabel(contact.language)}
                <SourceNote source={contact.languageSource} />
              </dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-muted">Country</dt>
              <dd className="m-0 text-right" data-testid="contact-country">
                {countryLabel(contact.country)}
                <SourceNote source={contact.countrySource} />
              </dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-muted">City</dt>
              <dd className="m-0 text-right" data-testid="contact-city">
                {contact.city ?? '—'}
              </dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-muted">Postal code</dt>
              <dd className="m-0 text-right" data-testid="contact-postal-code">
                {contact.postalCode ?? '—'}
              </dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-muted">Region</dt>
              <dd className="m-0 text-right" data-testid="contact-region">
                {contact.region ?? '—'}
              </dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-muted">Time zone</dt>
              <dd className="m-0 text-right" data-testid="contact-timezone">
                {contact.timezone ?? '—'}
              </dd>
            </div>
            {contact.mailchimp ? (
              <div className="flex justify-between gap-4" data-testid="contact-mailchimp">
                <dt className="text-muted">Mailchimp</dt>
                <dd className="m-0 text-right">
                  {contact.mailchimp.memberRating !== null
                    ? `Rating ${contact.mailchimp.memberRating}`
                    : 'No rating'}
                  {contact.mailchimp.leid ? ` · LEID ${contact.mailchimp.leid}` : ''}
                  {contact.mailchimp.euid ? ` · EUID ${contact.mailchimp.euid}` : ''}
                  {contact.mailchimp.lastChangedAt
                    ? ` · changed ${formatDateTime(contact.mailchimp.lastChangedAt)}`
                    : ''}
                </dd>
              </div>
            ) : null}
            {Object.entries(data.fields ?? {}).map(([key, value]) => (
              <div key={key} className="flex justify-between gap-4" data-testid="contact-field">
                <dt className="text-muted font-mono">{key}</dt>
                <dd className="m-0 text-right break-all">{value}</dd>
              </div>
            ))}
            <div className="flex justify-between gap-4">
              <dt className="text-muted">Source</dt>
              <dd className="m-0 text-right">{contact.source}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-muted">Consent</dt>
              <dd className="m-0 text-right">
                {consentLabel(contact.consentCategory, contact.orderSignupAt)}
              </dd>
            </div>
            {contact.orderSignupAt !== null ? (
              <div className="flex justify-between gap-4">
                <dt className="text-muted">Added by an order</dt>
                <dd className="m-0 text-right">{formatDateTime(contact.orderSignupAt)}</dd>
              </div>
            ) : null}
            <div className="flex justify-between gap-4">
              <dt className="text-muted">Created</dt>
              <dd className="m-0 text-right">{formatDateTime(contact.createdAt)}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-muted">Last sent to</dt>
              <dd className="m-0 text-right">
                {contact.lastSentAt ? formatDateTime(contact.lastSentAt) : '—'}
              </dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-muted">Last order</dt>
              <dd className="m-0 text-right">
                {contact.lastOrderAt ? formatDateWithYear(contact.lastOrderAt) : '—'}
              </dd>
            </div>
          </dl>
        </Card>

        <Card className="p-4">
          <h2 className="text-sm font-semibold m-0 mb-3">Consent proof</h2>
          {consent.length === 0 ? (
            <p className="text-muted text-sm m-0">No consent proof recorded.</p>
          ) : (
            <ul className="m-0 p-0 list-none flex flex-col gap-3">
              {consent.map((entry, i) => (
                <li
                  key={`${entry.at}-${i}`}
                  className="text-sm border-b border-border-soft last:border-b-0 pb-2.5 last:pb-0"
                >
                  <div className="flex justify-between">
                    <span className="font-medium">{entry.action}</span>
                    <span className="text-muted text-xs">{formatDateTime(entry.at)}</span>
                  </div>
                  <div className="text-muted text-xs mt-0.5">
                    Source: {entry.source}
                    {entry.textVersion ? ` · text ${entry.textVersion}` : ''}
                  </div>
                  <p className="m-0 mt-1 text-[13px]">{entry.text}</p>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      {data.predictions ? <PredictionCard prediction={data.predictions} /> : null}

      <ContactTags contactId={contact.id} labels={data.labels} />

      <ContactCustomObjects contactId={contact.id} />

      <section
        aria-labelledby="history-heading"
        className="border border-border rounded-xl overflow-hidden"
      >
        <h2
          id="history-heading"
          className="m-0 px-4 py-3 text-sm font-semibold border-b border-border-soft"
        >
          History
        </h2>
        {history.length === 0 ? (
          <div className="px-4 py-6 text-muted text-sm">No history yet.</div>
        ) : (
          <ol className="m-0 p-0 list-none">
            {history.map((h, i) => (
              <li
                key={`${h.at}-${i}`}
                className="flex items-center gap-3 px-4 py-2.5 border-b border-border-soft last:border-b-0 text-sm"
              >
                <span className="w-[90px] text-muted text-xs">{formatDateTime(h.at)}</span>
                <span className="w-[90px] font-medium">{HISTORY_LABELS[h.type] ?? h.type}</span>
                <Link to={`/kampagner/${h.campaignId}`} className="text-link">
                  {h.campaignName}
                </Link>
                {h.detail ? <span className="text-muted ml-auto">{h.detail}</span> : null}
              </li>
            ))}
          </ol>
        )}
      </section>
    </>
  );
}
